/**
 * ceremony-steps.ts — which screens a given instrument's signing ceremony has, and in what
 * order.
 *
 * The in-app ceremony used to be five steps for everything. Two of the instruments now say,
 * inside the bytes their signer seals, that something happened BEFORE and AROUND the
 * document — and the app has to do those things rather than merely assert them:
 *
 *   disclose  Only a hire purchase Schedule states a Cash price beside a Hire-purchase price
 *             and has the Driver confirm both "were stated to the Driver, orally and in
 *             writing and OTHERWISE THAN IN THIS AGREEMENT, BEFORE THIS AGREEMENT WAS MADE".
 *             That is section 2 of the Hire-Purchase Act, 1974 (NRCD 292), and "otherwise
 *             than in this Agreement" is why the step is FIRST — ahead of the plain-English
 *             key terms and well ahead of the document. An advance and a deposit make no
 *             such statement, so a disclosure screen on one would be the app inventing a
 *             formality its own instrument never claimed.
 *   booklet   The hire purchase and both advances are a Schedule PLUS a standing Terms
 *             booklet, and clause 3.3 of each says a copy "was shown to and accepted by the
 *             Driver / Borrower in the Primelane application before this Schedule was
 *             signed". It sits immediately after the read, because it is the second half of
 *             the same document and belongs beside it rather than beside the signature.
 *
 * ── THE ID `terms` IS THE CONFIRMATION CHECKLIST, NOT THE BOOKLET ─────────────
 * It has meant the tick-each-key-term step since the ceremony existed, and it is PERSISTED:
 * `CeremonyDraft.step` in esign.ts is written into a signer's localStorage. Renaming it to
 * free the word for the booklet would orphan every draft in flight — a resumed ceremony
 * dropped back to the beginning, for a rename. The booklet is `booklet`.
 *
 * ── WHY THIS IS A MODULE AND NOT A CONSTANT IN THE PAGE ───────────────────────
 * Because the ordering is the substance. "The disclosure comes before the agreement" and
 * "the booklet comes after the Schedule" are legal claims about a sequence, and a page-local
 * array is a claim nothing can check. Here they are pinned by a test that runs on every
 * commit, for all five instruments at once.
 */

export type Step = 'disclose' | 'intro' | 'read' | 'booklet' | 'terms' | 'sign' | 'done';

/** The five every document has. A deposit's and a witness's ceremony is exactly this. */
export const BASE_STEPS: readonly Step[] = ['intro', 'read', 'terms', 'sign', 'done'];

/** The instruments that state two prices and say both were disclosed beforehand. */
const DISCLOSES: ReadonlySet<string> = new Set(['HIRE_PURCHASE']);

/** The instruments that are a Schedule plus a booklet. The other two stand alone. */
const INCORPORATES_TERMS: ReadonlySet<string> = new Set([
    'HIRE_PURCHASE',
    'LOAN',
    'INSURANCE_LOAN',
]);

/** What each Schedule calls the person signing it, for copy that speaks the instrument. */
export const PARTY_NOUNS: Record<string, string> = {
    HIRE_PURCHASE: 'Driver',
    LOAN: 'Borrower',
    INSURANCE_LOAN: 'Borrower',
};

/**
 * The steps THIS document has.
 *
 * ── THE BOOKLET NEEDS BOTH CONDITIONS ─────────────────────────────────────────
 * `hasCitation` is whether clause 3.1 of the frozen body actually names a booklet. Each
 * condition catches what the other cannot:
 *
 *   · the doc type alone would put a booklet step on a Schedule that has no citation to
 *     show — which is every pre-registry body still sitting on staging, and a step whose
 *     entire content is "there is nothing here" is worse than no step;
 *   · the citation alone would let a citation-shaped run of text in some other body insert
 *     a step into a ceremony that incorporates nothing.
 *
 * `disclose` takes no such condition: the disclosure is made from the contract's own figures
 * and is owed whether or not they resolve. A hire purchase whose Cash price cannot be read
 * still shows the screen, in the form that states no figure — the panel degrades, the step
 * does not disappear.
 */
export function ceremonySteps(
    docType: string | null | undefined,
    hasCitation: boolean,
): readonly Step[] {
    const steps = [...BASE_STEPS];
    if (docType && INCORPORATES_TERMS.has(docType) && hasCitation) {
        steps.splice(steps.indexOf('read') + 1, 0, 'booklet');
    }
    if (docType && DISCLOSES.has(docType)) steps.unshift('disclose');
    return steps;
}
